import { FeatureFlagStore } from '@/stores/featureFlagStore';
import { FeatureFlags } from '@/constants/FeatureFlags';
import MenuItem from '@/models/menuItem';
import { UserRoles } from '@/constants';
import { UserStore as useUserStore } from '@/stores';
import { WalletStore } from '@/stores/walletStore';
import { Icons, Routes } from '@/utils';
import type { MenuOption } from 'naive-ui';
import { defineComponent } from 'vue';

import router from '@/router/router';
import { CaptureEvent, Events } from '@/services/amplitudeService';

// Função auxiliar para verificar papéis
function hasRole(userStore: any, ...roles: string[]): boolean {
	return roles.some((role) => userStore[role]);
}

// Função para construir o submenu de gerenciamento
function buildSubMenuManagement(userStore: any): MenuOption[] {
	const items: MenuOption[] = [];
	if (
		userStore.canReadAsAdmin ||
		userStore.isBusinessConsultant ||
		userStore.isBusinessManager ||
		userStore.isBusinessHead
	) {
		items.push(MenuItem.create('Parceiros', Routes.Brokers));
		items.push(
			MenuItem.create('Ferramentas de Portabilidade', Routes.PortabilityTools, Icons.Sheets),
		);
	}
	if (
		userStore.canReadAsAdmin ||
		userStore.isBrokerAdmin ||
		userStore.isBusinessConsultant ||
		userStore.isBusinessManager ||
		userStore.isBusinessHead
	) {
		items.push(MenuItem.create('Usuários', Routes.ManagementUsers));
	}
	if (userStore.canReadAsAdmin) {
		items.push(MenuItem.create('Busca de Clientes', Routes.ManagementClientsSearch));
		items.push(MenuItem.create('Política de Crédito', Routes.ManagementCreditPolicyView));
		items.push(MenuItem.create('Griffin', Routes.GriffinView));
		items.push(MenuItem.create('Parametrização', Routes.ManagementParameters, Icons.Settings));
		items.push(MenuItem.create('Gestão de Políticas', Routes.PolicyManager, Icons.Settings));
		items.push(MenuItem.create('Produtos', Routes.ProductsView, Icons.Product));
		items.push(
			MenuItem.create('Empresas (Cobrança)', Routes.ManagementManagers, Icons.GroupOutlined),
		);
		items.push(MenuItem.create('Escritórios', Routes.ManagementLawFirms, Icons.Settings));
	}
	if (userStore.canReadAsAdmin || userStore.isUser) {
		items.push(MenuItem.create('Financeiro', Routes.ManagementFinancialBrokerBills));
	}
	// Ratings por parceiro: só ADMIN, os mesmos papéis que a API aceita.
	if (userStore.isUserAdmin) {
		items.push(MenuItem.create('Ratings por parceiro', Routes.ManagementBrokerRatings));
		items.push(MenuItem.create('Produtos por parceiro', Routes.ManagementBrokerProductRatings));
	}
	return items;
}

/**
 * Financeiro do lado do parceiro. As faturas são do administrador do parceiro; a Carteira é de
 * todo usuário de parceiro do piloto do split (cada um vê o próprio setup).
 */
function buildFinancialMenu(
	userStore: ReturnType<typeof useUserStore>,
	walletEnabled: boolean,
): MenuOption | undefined {
	const wallet = MenuItem.create('Carteira', Routes.BrokerFinancialWallet, Icons.Wallet);

	if (userStore.isBrokerAdmin && walletEnabled) {
		return MenuItem.create('Financeiro', null, Icons.Financial, [
			MenuItem.create('Faturas', Routes.BrokerFinancial),
			wallet,
		]);
	}

	if (userStore.isBrokerAdmin) return MenuItem.create('Financeiro', Routes.BrokerFinancial, Icons.Financial);

	if (walletEnabled) return MenuItem.create('Financeiro', null, Icons.Financial, [wallet]);

	return undefined;
}

// Função para construir o menu principal
export function buildMenuOptions(
	userStore: ReturnType<typeof useUserStore>,
	walletEnabled = false,
): MenuOption[] {
	const subMenuManagement = buildSubMenuManagement(userStore);
	return [
		MenuItem.create('Inicio', Routes.Home, Icons.Home),
		MenuItem.create('Insights', Routes.Intelligence, Icons.Insights),
		MenuItem.create('Produtos', null, Icons.Product, [
			MenuItem.create(
				'Crédito Imobiliário',
				Routes.RealEstateFinancingCases,
				Icons.MortgageProduct,
			),
			MenuItem.create('Garantia Locatícia', null, Icons.Inbox, [
				MenuItem.create('Propostas', Routes.Cases, Icons.Proposal),
				MenuItem.create('Contratos', Routes.Contracts, Icons.Contract),
				// Fila de pré-auditoria: só quem decide (ADMIN/USER, a mesma matriz da API). O
				// parceiro externo lê como admin em outras telas, mas aqui a API o recusa.
				...(userStore.isUserAdmin || userStore.isUser
					? [MenuItem.create('Pré-auditoria', Routes.PreAudits, Icons.PreAudit)]
					: []),
			]),
			MenuItem.create('Seguro Incêndio', Routes.FireInsuranceCases, Icons.Contract),
			// Piloto do multicálculo: o item só aparece com a flag ligada. Sem router guard na
			// casa, a barreira real é o [Authorize] do backend — a flag só esconde o menu.
			...(FeatureFlagStore().isEnabled(FeatureFlags.SuretyInsurance)
				? [MenuItem.create('Seguro Fiança', Routes.SuretyInsuranceCases, Icons.Contract)]
				: []),
		]),
		userStore.canReadAsAdmin
			? MenuItem.create('Jurídico', null, Icons.Legal, [
					MenuItem.create('Ocorrências e Acordos', Routes.Legal),
					MenuItem.create('Cobranças', Routes.Collections),
					MenuItem.create('Mensagens recebidas', Routes.TwilioInbox, Icons.Whatsapp),
				])
			: MenuItem.create('Jurídico', Routes.Legal, Icons.Legal),
		UserRoles.CancellationReaders.includes(userStore.role)
			? MenuItem.create('Cancelamentos', Routes.CancellationRequests, Icons.Delete)
			: undefined,
		MenuItem.create('Relatórios', Routes.Reports, Icons.Sheets),
		buildFinancialMenu(userStore, walletEnabled),
		userStore.isUserAdmin ||
		userStore.isExternalPartner ||
		userStore.isBrokerAdmin ||
		userStore.isBusinessConsultant ||
		userStore.isBusinessManager ||
		userStore.isBusinessHead
			? MenuItem.create('Gerenciamento', null, Icons.GroupOutlined, subMenuManagement)
			: undefined,
	].filter(Boolean) as MenuOption[];
}

export const AppMenu = defineComponent({
	data: () => ({
		searchQuery: '',
		activeKey: '',
	}),

	computed: {
		/**
		 * @deprecated Use menuOptions instead
		 */
		menuOptions() {
			const userStore = useUserStore();
			return buildMenuOptions(userStore, WalletStore().enabled);
		},
		currentTheme() {
			const userStore = useUserStore();
			return userStore.theme;
		},
	},

	created() {
		// A Carteira só aparece para parceiro do piloto, e quem sabe disso é a API.
		const userStore = useUserStore();
		this.$watch(
			() => userStore.userClaims.Id,
			(userId: string) => void WalletStore().load(userId, userStore.role),
			{ immediate: true },
		);
	},

	methods: {
		handleSearch(query: string) {
			if (query) router.push({ name: Routes.Search, query: { query } });
		},
		toggleTheme() {
			const userStore = useUserStore();
			userStore.toggleTheme();
			CaptureEvent(Events.ToggleTheme, { theme: userStore.theme });
		},
	},
});
